import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { SkeletonRows } from "../components/UI/DesignStandards";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [message, setMessage] = useState("Verifying your email...");

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check if there's an auth token in the hash
        const hash = window.location.hash;
        
        if (hash && (hash.includes("access_token") || hash.includes("type=magiclink") || hash.includes("type=signup"))) {
          // Wait for Supabase to process the hash tokens
          // The detectSessionInUrl option should handle this automatically
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Get the current session (Supabase should have processed hash tokens by now)
        let session = null;
        let sessionError = null;
        
        // If we have hash tokens, try to manually extract and set the session
        if (hash && hash.includes("access_token")) {
          try {
            // Parse hash parameters
            const hashParams = new URLSearchParams(hash.substring(1)); // Remove #
            const accessToken = hashParams.get("access_token");
            const refreshToken = hashParams.get("refresh_token");
            const tokenType = hashParams.get("token_type");
            const expiresIn = hashParams.get("expires_in");
            
            if (accessToken && refreshToken) {
              const { data: sessionData, error: setSessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              
              if (setSessionError) {
                console.error("Error setting session from hash:", setSessionError);
              } else if (sessionData?.session) {
                session = sessionData.session;
              }
            }
          } catch (parseError) {
            console.error("Error parsing hash tokens:", parseError);
          }
        }
        
        // If we still don't have a session, try getSession (in case detectSessionInUrl worked)
        if (!session) {
          const result = await supabase.auth.getSession();
          
          if (result.data?.session?.user) {
            session = result.data.session;
          } else if (result.error) {
            sessionError = result.error;
          }
        }

        if (sessionError) {
          console.error("Session error:", sessionError);
          navigate("/signin");
          return;
        }

        if (!session?.user) {
          console.error("No session or user found after retries");
          navigate("/signin");
          return;
        }

        setMessage("Checking for pending invitations...");

        // Check if user has any pending invites
        const { data: invites, error: invitesError } = await supabase
          .from("organizationInvites")
          .select("id, organizationID")
          .eq("email", session.user.email)
          .eq("status", "pending");

        if (invitesError) {
          console.error("Error checking invites:", invitesError);
          navigate("/organization");
          return;
        }

        if (invites && invites.length > 0) {
          setMessage("Redirecting to your invitation...");

          // Redirect to the invite page so user can see details and accept
          const invite = invites[0];
          setTimeout(() => {
            navigate(`/invite/${invite.id}`);
          }, 1500);
          return;
        }

        // No pending invites, redirect to organization page
        setMessage("Welcome! Redirecting to organization page...");
        setTimeout(() => {
          navigate("/organization");
        }, 1500);
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/signin");
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24">
              <SkeletonRows count={1} />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {message}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please wait while we process your request.
          </p>
        </div>
      </div>
    </div>
  );
}
